<script setup lang="ts">
import { computed } from 'vue';
import { fosterCartLinePetId } from '@ShoppingCartModule';

const emit = defineEmits<{
  removeAnimal: [animal: any, index: number];
}>();

const props = defineProps<{
  animals: any[];
  canRemoveAnimalRows?: boolean;
  canReserveKennelSpot: boolean;
  formTouched: boolean;
  fosterContractsEnabled: boolean;
  fosterWaiversEnabled: boolean;
  startFosterReasonRequired: boolean;
  startFosterReasons: any[];
  waiverOptionsWithId: any[];
}>();

const animalOverrides = defineModel<Record<string, { waiver_id?: string; waiver_ids?: string[]; notes?: string }>>(
  'animalOverrides',
  {
    required: true,
  }
);
const fosterStartReasonErrors = defineModel<Record<string, boolean>>('fosterStartReasonErrors', {
  required: true,
});

const rowFosterStartReasonError = computed(() => {
  const errors = fosterStartReasonErrors.value;
  const touched = props.formTouched;

  return (animal: any) => {
    const id = fosterCartLinePetId(animal);
    if (id === '') return false;

    return touched && !!errors[id];
  };
});
const animalColumnStyle = computed(() => {
  if (props.fosterContractsEnabled) return { minWidth: '13rem', width: '13rem' };
  return { minWidth: '16rem', width: '18rem' };
});
const reserveKennelSpotColumnStyle = computed(() => {
  if (props.fosterContractsEnabled) return { minWidth: '9rem', width: '16rem' };
  return { minWidth: '9rem', width: '20rem' };
});
const fosterNotesColumnStyle = computed(() => {
  if (props.fosterContractsEnabled) return { width: '100%' };
  return { minWidth: '14rem' };
});

function overrideFor(animal: any) {
  const key = fosterCartLinePetId(animal);
  if (key === '') return {};

  if (!animalOverrides.value[key]) animalOverrides.value[key] = {};
  return animalOverrides.value[key];
}

function collectWaiverIdsFromList(list: unknown): string[] {
  if (!Array.isArray(list)) return [];
  const ids: string[] = [];

  for (const item of list) {
    if (item == null) continue;
    if (typeof item === 'string') {
      if (item) ids.push(item);
      continue;
    }
    if (typeof item === 'number') {
      ids.push(`${item}`);
      continue;
    }

    const oid = item?._id?.$oid ?? item?._id ?? item?.id?.$oid ?? item?.id;
    if (oid == null || oid === '') continue;

    ids.push(typeof oid === 'string' ? oid : `${oid}`);
  }
  return ids.filter(Boolean);
}

function waiverIdsForAnimal(animal: any): string[] {
  const key = fosterCartLinePetId(animal);
  if (key === '') return [];

  const override = animalOverrides.value[key];
  const hasExplicitWaiverIds = override != null && Object.prototype.hasOwnProperty.call(override, 'waiver_ids');

  let raw: string[] = [];
  if (hasExplicitWaiverIds) {
    const waiverIdsFromOverride = override.waiver_ids ?? [];
    raw = waiverIdsFromOverride;
  }
  if (!hasExplicitWaiverIds && override?.waiver_id) {
    const legacySingleWaiverId = override.waiver_id;
    raw = [legacySingleWaiverId];
  }
  if (!hasExplicitWaiverIds && !override?.waiver_id) {
    const waiverIdsFromIntake = collectWaiverIdsFromList(animal.intake_waivers);
    const waiverIdsFromAnimal = collectWaiverIdsFromList(animal.waivers);
    const defaultWaiverIds = [...new Set([...waiverIdsFromIntake, ...waiverIdsFromAnimal])];

    if (defaultWaiverIds.length) raw = defaultWaiverIds;
  }

  const allowedWaiverIds = new Set(props.waiverOptionsWithId.map((waiverOption: any) => String(waiverOption.id)));

  return raw
    .map((waiverId) => String(waiverId))
    .filter((waiverId) => waiverId !== '' && allowedWaiverIds.has(waiverId));
}

function setWaiverIds(animal: any, ids: unknown) {
  const key = fosterCartLinePetId(animal);
  if (key === '') return;

  const normalized = collectWaiverIdsFromList(ids);
  const next = { ...animalOverrides.value };
  const prev = { ...(next[key] || {}) };

  prev.waiver_ids = normalized;
  delete prev.waiver_id;

  next[key] = prev;
  animalOverrides.value = next;
}

function selectedWaiverRows(animal: any): Array<{ id: string; title: string }> {
  const ids = waiverIdsForAnimal(animal);
  const opts = props.waiverOptionsWithId;

  return ids
    .map((waiverId) => {
      const matched = opts.find((option: any) => option.id == waiverId);
      if (!matched) return null;

      const label = matched.title ?? matched.name ?? matched.label ?? matched.id ?? '';
      return {
        id: `${matched.id}`,
        title: `${label}`,
      };
    })
    .filter((row): row is { id: string; title: string } => row != null);
}

function startReasonModel(animal: any): any {
  return (
    props.startFosterReasons.find(
      (reason) => reason._id === (animal.start_foster_reason_id?.$oid || animal.start_foster_reason_id)
    ) ?? null
  );
}

function setStartReason(animal: any, selected: any) {
  const key = fosterCartLinePetId(animal);

  if (!selected) {
    animal.start_foster_reason_id = null;
    animal.start_foster_reason = null;
    return;
  }

  animal.start_foster_reason_id = selected._id;
  animal.start_foster_reason = selected.label ?? selected.name ?? '';
  const next = { ...fosterStartReasonErrors.value };

  delete next[key];
  fosterStartReasonErrors.value = next;
}
</script>

<template>
  <div class="foster-animal-details-table overflow-hidden rounded-md border">
    <DataTable
      class="foster-animal-details-datatable text-sm"
      responsive-layout="scroll"
      :value="animals"
    >
      <Column
        :headerStyle="animalColumnStyle"
        :header="$t('cart.label.fosterAnimal')"
        :style="animalColumnStyle"
      >
        <template #body="{ data: cartAnimal }">
          <MAnimalDetailsCell
            :animal="cartAnimal"
            :animalProfileId="fosterCartLinePetId(cartAnimal) || undefined"
          />
        </template>
      </Column>

      <Column class="w-72">
        <template #header>
          <span>
            {{ $t('cart.label.fosterStartReason') }}
          </span>

          <span
            v-if="startFosterReasonRequired"
            class="text-red-600"
          >
            *
          </span>
        </template>

        <template #body="{ data: cartAnimal }">
          <div class="flex flex-col gap-1">
            <AFieldDropdown
              class="w-[240px]"
              optionLabel="label"
              :error="rowFosterStartReasonError(cartAnimal)"
              :modelValue="startReasonModel(cartAnimal)"
              :options="startFosterReasons"
              :placeholder="$t('cart.label.fosterStartReason')"
              @update:modelValue="(selected: any) => setStartReason(cartAnimal, selected)"
            />

            <AFieldError
              v-if="rowFosterStartReasonError(cartAnimal)"
              error="components.forms.errors.required"
            />
          </div>
        </template>
      </Column>

      <Column
        v-if="canReserveKennelSpot"
        :headerStyle="reserveKennelSpotColumnStyle"
        :header="$t('cart.label.fosterReserveKennelSpot')"
        :style="reserveKennelSpotColumnStyle"
      >
        <template #body="{ data: cartAnimal }">
          <InputSwitch
            :modelValue="!!cartAnimal.reserve_kennel_spot"
            @update:modelValue="(checked: boolean) => (cartAnimal.reserve_kennel_spot = checked)"
          />
        </template>
      </Column>

      <Column
        v-if="fosterWaiversEnabled"
        class="min-w-72"
        :header="$t('cart.label.fosterWaivers')"
      >
        <template #body="{ data: cartAnimal }">
          <div class="flex max-w-[256px] flex-col gap-2">
            <AFieldMultiSelect
              v-if="waiverOptionsWithId.length"
              class="min-h-9"
              optionLabel="title"
              optionValue="id"
              :maxSelectedLabels="1"
              :modelValue="waiverIdsForAnimal(cartAnimal)"
              :options="waiverOptionsWithId"
              :placeholder="$t('cart.label.fosterWaivers')"
              @update:modelValue="(ids: unknown) => setWaiverIds(cartAnimal, ids)"
            />

            <p
              v-else
              class="w-[240px] py-1 text-xs italic text-gray-500"
            >
              {{ $t('components.emptyMessage.dropdown') }}
            </p>

            <ul
              v-if="selectedWaiverRows(cartAnimal).length"
              class="m-0 flex list-none flex-col gap-1 p-0"
            >
              <li
                v-for="row in selectedWaiverRows(cartAnimal)"
                class="rounded-md bg-slate-100 px-2 py-1 text-xs font-medium leading-snug text-gray-800"
                :key="row.id"
              >
                {{ row.title }}
              </li>
            </ul>
          </div>
        </template>
      </Column>

      <Column
        class="foster-notes-column min-w-48"
        :headerStyle="fosterNotesColumnStyle"
        :header="$t('cart.label.fosterNotes')"
        :style="fosterNotesColumnStyle"
      >
        <template #body="{ data: cartAnimal }">
          <AFieldTextArea
            class="min-h-[60px] w-full max-w-full resize"
            placeholder="cart.label.fosterNotes"
            rows="2"
            :modelValue="overrideFor(cartAnimal).notes ?? ''"
            @update:modelValue="(text: string | undefined) => (overrideFor(cartAnimal).notes = text || undefined)"
          />
        </template>
      </Column>

      <Column
        v-if="canRemoveAnimalRows"
        class="w-14 shrink-0"
        header=""
      >
        <template #body="{ data: cartAnimal, index: rowIndex }">
          <Button
            v-tooltip.top="$t('common.remove')"
            rounded
            text
            class="af-button !p-2"
            icon="pi pi-trash"
            severity="danger"
            type="button"
            @click="emit('removeAnimal', cartAnimal, rowIndex)"
          />
        </template>
      </Column>
    </DataTable>
  </div>
</template>

<style scoped>
.foster-animal-details-datatable :deep(.p-datatable-thead > tr > th) {
  background-color: rgb(241 245 249) !important;
}

.foster-animal-details-datatable :deep(.p-datatable-tbody > tr > td) {
  vertical-align: top;
}

.foster-animal-details-datatable :deep(.p-datatable-thead > tr > th.foster-notes-column),
.foster-animal-details-datatable :deep(.p-datatable-tbody > tr > td.foster-notes-column) {
  min-width: 12rem;
}
</style>
